import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment.development';
import {
  Auth,
  GrantResponse,
  LoggedInUser,
  LoginPayload,
  LoginResponse,
} from '../interfaces/auth.interface';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService {
  auth$: Observable<Auth | undefined>;
  private _authSource: BehaviorSubject<Auth | undefined>;

  sigma$: Observable<string | undefined>;
  private _sigmaSource: BehaviorSubject<string | undefined>;

  alpha$: Observable<string | undefined>;
  private _alphaSource: BehaviorSubject<string | undefined>;

  beta$: Observable<string | undefined>;
  private _betaSource: BehaviorSubject<string | undefined>;

  bc = new BroadcastChannel('iAuth');

  constructor(private _cookieServ: CookieService) {
    super();

    this._authSource = new BehaviorSubject<Auth | undefined>(
      this._storage.getItem('auth')
    );
    this.auth$ = this._authSource.asObservable();

    this._sigmaSource = new BehaviorSubject<string | undefined>(
      this._storage.getItem('sigma')
    );
    this.sigma$ = this._sigmaSource.asObservable();

    this._alphaSource = new BehaviorSubject<string | undefined>(
      this._storage.getItem('alpha')
    );
    this.alpha$ = this._alphaSource.asObservable();

    this._betaSource = new BehaviorSubject<string | undefined>(
      this._storage.getItem('beta')
    );
    this.beta$ = this._betaSource.asObservable();
  }

  get sigmaValue() {
    return this._sigmaSource.value;
  }
  get alphaValue() {
    return this._alphaSource.value;
  }
  get betaValue() {
    return this._betaSource.value;
  }
  get authValue() {
    return this._authSource.value;
  }
  get currentUser() {
    return this.authValue?.currentUser;
  }
  get users() {
    return this.authValue?.users;
  }
  setAuth(auth: Auth) {
    this._storage.setItem('auth', auth);
    this._authSource.next(auth);
    if (auth.users[auth.currentUser])
      this._cookieServ.set('pookie', auth.users[auth.currentUser].token, {
        domain: environment.cookieDomain,
        path: '/',
        secure: true,
        sameSite: 'None',
      });
    else this._cookieServ.delete('pookie');
  }
  setSigma(value: string | undefined) {
    if (value) this._storage.setItem('sigma', value);
    else this._storage.removeItem('sigma');
    this._sigmaSource.next(value);
  }
  setAlpha(value: string | undefined) {
    if (value) this._storage.setItem('alpha', value);
    else this._storage.removeItem('alpha');
    this._alphaSource.next(value);
  }
  setBeta(value: string | undefined) {
    if (value) this._storage.setItem('beta', value);
    else this._storage.removeItem('beta');
    this._betaSource.next(value);
  }
  setCurrentUser(userId: string) {
    const auth = {
      ...(this.authValue || { currentUser: '', users: {} }),
    };
    auth.currentUser = userId;
    this.setAuth(auth);
  }
  addUser(user: LoggedInUser) {
    const auth = { ...(this.authValue || { currentUser: '', users: {} }) };
    auth.currentUser = user.user.id;
    auth.users[user.user.id] = user;
    this.setAuth(auth);
  }
  updateToken(token: string) {
    const auth = { ...(this.authValue || { currentUser: '', users: {} }) };
    const u = this.currentUser;
    const users = this.users;
    if (u && users) {
      const user = users[u];
      if (user) {
        user.token = token;
        this.setAuth({ ...auth, users });
      }
    }
  }
  removeUser(userId: string) {
    const auth = { ...(this.authValue || { currentUser: '', users: {} }) };
    delete auth.users[userId];
    const users = Object.values(auth.users);
    auth.currentUser = users[0]?.user?.id || '';
    this.setAuth(auth);
  }

  checkByUsername(Usernames: string) {
    return this._http.post<boolean>(`/api/User/CheckByUsername`, null, {
      responseType: 'json',
      params: { Usernames },
    });
  }
  login(payload: LoginPayload) {
    return this._http.post<LoginResponse>(`/api/User/Login`, payload, {
      responseType: 'json',
    });
  }
  grant(grantToken: string) {
    return this._http.patch<GrantResponse>('/api/User/Grant', null, {
      responseType: 'json',
      params: { grantToken },
    });
  }
  revokeToken(userId: string, token: string) {
    const users = this.users;
    let refreshToken;
    const params: Params = {};
    if (userId && users) {
      refreshToken = users[userId]?.refreshToken;
    }
    let headers = new HttpHeaders();
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    if (refreshToken) params['refreshToken'] = refreshToken;
    return this._http.patch('/api/User/revoke-token', null, {
      responseType: 'json',
      params,
      headers,
    });
  }
  forgot(username: string) {
    return this._http.post<any>(
      `/api/User/ForgotPassword?username=${username}`,
      null,
      {
        responseType: 'json',
      }
    );
  }

  reset(payload: any) {
    return this._http.post<any>(`/api/User/ResetPassword`, payload, {
      responseType: 'json',
    });
  }
}
